/**
 * DailyCheckinFields — presentational, controlled.
 *
 * iter111.6 · Adds per-field touch tracking. The parent passes an
 * `onFieldTouch(field)` callback which fires the first time the user
 * moves a slider or edits the comment/body-map. The parent uses this
 * set to build the submit payload as (savedDoc || pristine defaults) +
 * only the fields the user explicitly touched. That means adjusting one
 * slider never silently overwrites the other seven with pristine
 * defaults.
 */
import BodyMap from "@/components/BodyMap";

function Slider({ label, value, min = 0, max = 10, step = 1, onChange, hint, testid, touched }) {
  return (
    <label className="block" data-testid={`${testid}-wrap`} data-touched={touched ? "yes" : "no"}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="fl-label text-[11px]" style={{ color: "var(--fl-muted)" }}>{label}</span>
        <span className="fl-heading text-lg text-[color:var(--fl-orange)] tabular-nums" data-testid={`${testid}-value`}>
          {value == null ? "—" : value}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step}
        value={value == null ? min : value}
        onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
        className="w-full accent-[color:var(--fl-orange)] mt-1"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{hint}</div>}
    </label>
  );
}

function AdherenceReadout({ adherence, hasSchedule }) {
  const noSchedule = hasSchedule === false;
  return (
    <div className="block" data-testid="cin-adherence-readout">
      <div className="flex items-baseline justify-between gap-2">
        <span className="fl-label text-[11px]" style={{ color: "var(--fl-muted)" }}>Adherence · last 7 days</span>
        <span className="fl-heading text-lg text-[color:var(--fl-orange)] tabular-nums" data-testid="cin-adherence-value">
          {noSchedule || adherence == null ? "—" : `${adherence}%`}
        </span>
      </div>
      {noSchedule ? (
        <div className="text-[10px] mt-1" style={{ color: "var(--fl-muted-2)" }} data-testid="cin-adherence-none">No sessions scheduled</div>
      ) : (
        <div className="h-1 bg-white/10 rounded mt-2 overflow-hidden">
          <div className="h-full bg-[color:var(--fl-orange)]" style={{ width: `${adherence || 0}%` }} />
        </div>
      )}
      <div className="text-[10px] mt-1" style={{ color: "var(--fl-muted-2)" }}>Calculated automatically from your completed sessions.</div>
    </div>
  );
}

export default function DailyCheckinFields({ value, onChange, onExpandBodyMap, disabled = false, touchedFields, onFieldTouch, adherence = null, hasSchedule = null }) {
  const c = value || {};
  const isTouched = (k) => (touchedFields ? touchedFields.has(k) : true);
  const set = (k, v) => {
    if (onFieldTouch) onFieldTouch(k);
    onChange({ ...c, [k]: v });
  };
  return (
    <div className={disabled ? "opacity-70 pointer-events-none" : ""} data-testid="daily-checkin-fields">
      <div className="grid md:grid-cols-2 gap-4">
        <Slider label="Pain (0–10)"            value={c.pain}           min={0}  max={10}                 onChange={(v) => set("pain", v)}           testid="cin-pain"       touched={isTouched("pain")} />
        <Slider label="Sleep hours"            value={c.sleep_hours}    min={0}  max={16} step={0.5}      onChange={(v) => set("sleep_hours", v)}    testid="cin-sleep-h"    touched={isTouched("sleep_hours")} />
        <Slider label="Sleep quality (1–5)"    value={c.sleep_quality}  min={1}  max={5}                  onChange={(v) => set("sleep_quality", v)}  testid="cin-sleep-q"    hint="1 = poor · 5 = excellent"       touched={isTouched("sleep_quality")} />
        <Slider label="Energy (1–5)"           value={c.energy}         min={1}  max={5}                  onChange={(v) => set("energy", v)}         testid="cin-energy"     touched={isTouched("energy")} />
        <Slider label="Stress (1–5)"           value={c.stress}         min={1}  max={5}                  onChange={(v) => set("stress", v)}         testid="cin-stress"     hint="1 = calm · 5 = overwhelmed"     touched={isTouched("stress")} />
        <Slider label="Confidence in recovery" value={c.confidence}     min={1}  max={5}                  onChange={(v) => set("confidence", v)}     testid="cin-conf"       touched={isTouched("confidence")} />
        <Slider label="Exercise Difficulty (0 if none)" value={c.session_rpe ?? 0} min={0} max={10}        onChange={(v) => set("session_rpe", v)}    testid="cin-rpe"        hint="0 = none · 10 = maximal effort" touched={isTouched("session_rpe")} />
        <AdherenceReadout adherence={adherence} hasSchedule={hasSchedule} />
      </div>

      <div className="mt-5">
        <label className="fl-label text-[11px]" style={{ color: "var(--fl-muted)" }} htmlFor="cin-comments">
          Comments (optional)
        </label>
        <textarea
          id="cin-comments"
          className="fl-input mt-1"
          rows={3}
          maxLength={1000}
          placeholder="Anything you'd like your coach to know — pain, sleep, mood, sessions…"
          value={c.comments || ""}
          onChange={(e) => set("comments", e.target.value)}
          data-testid="cin-comments"
        />
        <div className="text-[10px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
          Shared with your AI Coach. Do not include emergencies — use the safety link above for those.
        </div>
      </div>

      <div className="mt-5" data-testid="cin-body-map">
        <div className="flex items-center justify-between mb-2">
          <div className="fl-label text-xs" style={{ color: "var(--fl-muted)" }}>
            Pain map · tap the body to mark where it hurts
          </div>
          {onExpandBodyMap && (
            <button
              type="button"
              onClick={onExpandBodyMap}
              className="text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full transition"
              style={{ color: "#f47a20", background: "transparent", border: "1px solid rgba(244, 122, 32, 0.28)" }}
              data-testid="cin-body-map-expand"
            >
              Expand
            </button>
          )}
        </div>
        <BodyMap value={c.pain_locations || []} onChange={(next) => set("pain_locations", next)} locked={false} testid="cin-body-map-svg" />
      </div>
    </div>
  );
}
